import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ActivityIndicator,
  TouchableOpacity,
  ScrollView,
} from 'react-native';
import { useNavigate } from 'react-router-dom';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../../lib/contexts/AuthContext';
import { DynamicTitle } from '../DynamicTitle';
import { COMENSAL_TAB_BAR_OVERLAY_INSET } from './ComensalBottomTabBar';
import { ComensalRutaMap } from './ComensalRutaMap';
import { getRestaurantes } from '../../database/database';
import { type CiudadListaItem } from '../../lib/services/comensal-ciudad';
import type { EdicionActivaBanner } from '../../lib/services/comensal-edicion';
import {
  fetchCiudadesListaCached,
  fetchEdicionEscaparateComensalCached,
} from '../../lib/cache/comensalReadCache';
import { getCurrentUserPosition } from '../../lib/utils/userGeolocation';
import { fetchOsrmDrivingRoute, straightLineRoute } from '../../lib/utils/osrmDrivingRoute';
import { collectEstablecimientoPins, nearestEstablecimientoPin } from '../../lib/utils/propuestaDistancia';
import type { RutaMapLatLng } from './ComensalRutaMap.types';

function fallbackCenterForCiudades(
  ciudadIds: string[],
  list: CiudadListaItem[]
): { lat: number; lng: number } {
  const setIds = new Set(ciudadIds.map((id) => parseInt(id, 10)).filter((n) => !Number.isNaN(n)));
  const centers = list.filter(
    (c) => setIds.has(c.id) && c.latitud_centro != null && c.longitud_centro != null
  );
  if (centers.length === 0) return { lat: 8.5, lng: -66.9 };
  const lat =
    centers.reduce((s, c) => s + (c.latitud_centro as number), 0) / centers.length;
  const lng =
    centers.reduce((s, c) => s + (c.longitud_centro as number), 0) / centers.length;
  return { lat, lng };
}

function ciudadNamesLabel(ciudadIds: string[], list: CiudadListaItem[]): string {
  const setIds = new Set(ciudadIds.map((id) => parseInt(id, 10)).filter((n) => !Number.isNaN(n)));
  const names = list.filter((c) => setIds.has(c.id)).map((c) => c.nombre);
  if (names.length === 0) return '';
  return names.join(', ');
}

export function ComensalRutaPage() {
  const navigate = useNavigate();
  const { user, profile, loading } = useAuth();
  const ciudadIds = useMemo(
    () => profile?.ciudades_ids?.filter(Boolean) ?? [],
    [profile?.ciudades_ids]
  );

  const [ciudadesList, setCiudadesList] = useState<CiudadListaItem[]>([]);
  const [loadingCiudades, setLoadingCiudades] = useState(true);
  const [userLatLng, setUserLatLng] = useState<{ lat: number; lng: number } | null>(null);
  const [ubicacionLista, setUbicacionLista] = useState<string | null>(null);
  const [mapCoordsReady, setMapCoordsReady] = useState(false);

  const [edicionBanner, setEdicionBanner] = useState<EdicionActivaBanner | null>(null);
  const [edicionActivaId, setEdicionActivaId] = useState<number | null>(null);
  const [edicionReady, setEdicionReady] = useState(false);

  const [restaurantes, setRestaurantes] = useState<Awaited<ReturnType<typeof getRestaurantes>>>([]);
  const [loadingRestaurantes, setLoadingRestaurantes] = useState(false);
  const [restaurantesError, setRestaurantesError] = useState<string | null>(null);

  const edicionIdParaRuta = edicionBanner?.id ?? edicionActivaId ?? null;

  const restaurantesEnEdicion = useMemo(() => {
    if (!edicionReady || edicionIdParaRuta == null) return [];
    const eid = String(edicionIdParaRuta);
    return restaurantes.filter((r) => r.ediciones_ids?.includes(eid));
  }, [restaurantes, edicionIdParaRuta, edicionReady]);

  const pins = useMemo(() => collectEstablecimientoPins(restaurantesEnEdicion), [restaurantesEnEdicion]);

  const pinsKey = useMemo(
    () => pins.map((p) => `${p.id}:${p.lat.toFixed(6)}:${p.lng.toFixed(6)}`).join('|'),
    [pins]
  );

  const [routeCoordinates, setRouteCoordinates] = useState<RutaMapLatLng[] | null>(null);

  const fallbackCenter = useMemo(
    () => fallbackCenterForCiudades(ciudadIds, ciudadesList),
    [ciudadIds, ciudadesList]
  );

  const ciudadEtiqueta = useMemo(() => ciudadNamesLabel(ciudadIds, ciudadesList), [ciudadIds, ciudadesList]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setLoadingCiudades(true);
    fetchCiudadesListaCached()
      .then((list) => {
        if (!cancelled) setCiudadesList(list);
      })
      .catch(() => {
        if (!cancelled) setCiudadesList([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCiudades(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setEdicionReady(false);
    fetchEdicionEscaparateComensalCached()
      .then(({ id, banner }) => {
        if (cancelled) return;
        setEdicionBanner(banner);
        setEdicionActivaId(id);
      })
      .catch(() => {
        if (!cancelled) {
          setEdicionBanner(null);
          setEdicionActivaId(null);
        }
      })
      .finally(() => {
        if (!cancelled) setEdicionReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setMapCoordsReady(false);
    setUbicacionLista(null);
    getCurrentUserPosition()
      .then((p) => {
        if (!cancelled) setUserLatLng({ lat: p.latitude, lng: p.longitude });
      })
      .catch((e) => {
        if (!cancelled) {
          setUserLatLng(null);
          setUbicacionLista(e instanceof Error ? e.message : 'Ubicación no disponible');
        }
      })
      .finally(() => {
        if (!cancelled) setMapCoordsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id || ciudadIds.length === 0) {
      setRestaurantes([]);
      setRestaurantesError(null);
      setLoadingRestaurantes(false);
      return;
    }
    let cancelled = false;
    setLoadingRestaurantes(true);
    setRestaurantesError(null);
    getRestaurantes(ciudadIds)
      .then((list) => {
        if (!cancelled) setRestaurantes(list);
      })
      .catch(() => {
        if (!cancelled) {
          setRestaurantes([]);
          setRestaurantesError('No se pudieron cargar los establecimientos');
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingRestaurantes(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, ciudadIds]);

  useEffect(() => {
    if (!userLatLng || pins.length === 0) {
      setRouteCoordinates(null);
      return;
    }
    const dest = nearestEstablecimientoPin(userLatLng, pins);
    if (!dest) {
      setRouteCoordinates(null);
      return;
    }
    const destPt = { lat: dest.lat, lng: dest.lng };
    /* Línea recta al instante para que siempre haya trazo rojo; OSRM lo sustituye por calles si responde. */
    setRouteCoordinates(straightLineRoute(userLatLng, destPt));

    let cancelled = false;
    fetchOsrmDrivingRoute(userLatLng, destPt)
      .then((line) => {
        if (cancelled) return;
        if (line && line.length >= 2) setRouteCoordinates(line);
      })
      .catch(() => {
        /* mantener la recta */
      });
    return () => {
      cancelled = true;
    };
  }, [userLatLng?.lat, userLatLng?.lng, pinsKey]);

  if (loading || !user || !profile) {
    return (
      <View style={styles.centered}>
        <ActivityIndicator size="large" color="#030213" />
      </View>
    );
  }

  if (ciudadIds.length === 0) {
    return (
      <ScrollView
        style={styles.screenScroll}
        contentContainerStyle={styles.screenContent}
        keyboardShouldPersistTaps="handled"
      >
        <DynamicTitle title="Ruta — As Gastronómico" />
        <Text style={styles.title}>Ruta</Text>
        <Text style={styles.sub}>
          Elegí al menos una ciudad en tu perfil para ver el mapa con los establecimientos participantes.
        </Text>
        <TouchableOpacity style={styles.btn} onPress={() => navigate('/perfil')} accessibilityRole="button">
          <Text style={styles.btnText}>Ir al perfil</Text>
          <Ionicons name="chevron-forward" size={20} color="#fff" />
        </TouchableOpacity>
      </ScrollView>
    );
  }

  const showMapLoader = loadingCiudades || loadingRestaurantes || !mapCoordsReady || !edicionReady;

  const avisoMapa = useMemo(() => {
    if (showMapLoader || restaurantesError || !edicionReady) return null;
    if (edicionIdParaRuta == null) {
      return 'No hay edición activa. Cuando la organización active una edición, verás aquí los establecimientos participantes.';
    }
    if (restaurantesEnEdicion.length === 0) {
      return 'No hay establecimientos de esta edición en las ciudades de tu perfil.';
    }
    if (pins.length === 0) {
      return 'Los establecimientos de esta edición aún no tienen ubicación en el mapa. Cuando tengan coordenadas en la ficha, aparecerán aquí.';
    }
    return null;
  }, [
    showMapLoader,
    restaurantesError,
    edicionReady,
    edicionIdParaRuta,
    restaurantesEnEdicion.length,
    pins.length,
  ]);
  const mapBlock =
    showMapLoader || restaurantesError ? (
      <View style={[styles.mapPlaceholder, restaurantesError ? styles.mapPlaceholderWarn : null]}>
        {showMapLoader ? (
          <ActivityIndicator size="large" color="#030213" />
        ) : (
          <Text style={styles.mapPlaceholderText}>{restaurantesError}</Text>
        )}
      </View>
    ) : (
      <ComensalRutaMap
        userLatLng={userLatLng}
        pins={pins}
        fallbackCenter={fallbackCenter}
        routeCoordinates={routeCoordinates}
      />
    );

  return (
    <View style={styles.screen}>
      <DynamicTitle title="Ruta — As Gastronómico" />
      <View style={styles.headerRow}>
        <View style={styles.headerTextWrap}>
          <Text style={styles.title}>Ruta</Text>
          {edicionBanner?.nombre ? (
            <Text style={styles.edicionLine} numberOfLines={2}>
              {edicionBanner.nombre}
            </Text>
          ) : edicionReady && edicionIdParaRuta != null ? (
            <Text style={styles.edicionLine} numberOfLines={1}>
              Edición en curso
            </Text>
          ) : null}
          {ciudadEtiqueta ? (
            <Text style={styles.ciudadLine} numberOfLines={2}>
              {ciudadEtiqueta}
            </Text>
          ) : null}
        </View>
      </View>
      {ubicacionLista ? (
        <Text style={styles.ubicHint} numberOfLines={2}>
          {ubicacionLista}
        </Text>
      ) : null}
      <View style={styles.legend}>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: '#60a5fa', borderColor: '#2563eb' }]} />
          <Text style={styles.legendText}>Tu ubicación</Text>
        </View>
        <View style={styles.legendItem}>
          <View style={[styles.legendDot, { backgroundColor: '#1e293b', borderColor: '#030213' }]} />
          <Text style={styles.legendText}>Establecimientos (edición)</Text>
        </View>
        {userLatLng && pins.length > 0 ? (
          <View style={styles.legendItem}>
            <View style={[styles.legendStripe, { backgroundColor: '#dc2626' }]} />
            <Text style={styles.legendText}>Ruta en auto al más cercano</Text>
          </View>
        ) : null}
      </View>
      {avisoMapa ? <Text style={styles.noPins}>{avisoMapa}</Text> : null}
      <View style={styles.mapShell}>{mapBlock}</View>
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: '#f9fafb',
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: COMENSAL_TAB_BAR_OVERLAY_INSET,
  },
  screenScroll: {
    flex: 1,
    backgroundColor: '#f9fafb',
  },
  screenContent: {
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: COMENSAL_TAB_BAR_OVERLAY_INSET,
  },
  centered: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f9fafb',
  },
  headerRow: {
    marginBottom: 8,
  },
  headerTextWrap: {
    gap: 4,
  },
  title: {
    fontSize: 22,
    fontWeight: '700',
    color: '#030213',
  },
  edicionLine: {
    fontSize: 16,
    color: '#030213',
    fontWeight: '600',
  },
  ciudadLine: {
    fontSize: 15,
    color: '#4b5563',
    fontWeight: '500',
  },
  sub: {
    fontSize: 15,
    color: '#6b7280',
    lineHeight: 22,
    marginBottom: 16,
  },
  ubicHint: {
    fontSize: 13,
    color: '#b45309',
    marginBottom: 8,
    lineHeight: 18,
  },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 10,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
    borderWidth: 2,
  },
  legendStripe: {
    width: 18,
    height: 4,
    borderRadius: 2,
  },
  legendText: {
    fontSize: 13,
    color: '#4b5563',
  },
  noPins: {
    fontSize: 14,
    color: '#6b7280',
    lineHeight: 20,
    marginBottom: 10,
  },
  mapShell: {
    flex: 1,
    minHeight: 320,
    marginTop: 4,
  },
  mapPlaceholder: {
    flex: 1,
    minHeight: 320,
    borderRadius: 16,
    backgroundColor: '#e5e7eb',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  mapPlaceholderWarn: {
    backgroundColor: '#fef3c7',
  },
  mapPlaceholderText: {
    fontSize: 15,
    color: '#92400e',
    textAlign: 'center',
  },
  btn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    backgroundColor: '#030213',
    paddingVertical: 14,
    paddingHorizontal: 20,
    borderRadius: 12,
    alignSelf: 'flex-start',
  },
  btnText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});
